import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ChangeDetectorRef, ViewChild, ElementRef
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil, debounceTime } from 'rxjs/operators';

import { ROUTES } from './vertical-menu-routes.config';
import { RouteInfo } from './vertical-menu.metadata';
import { AuthService } from '@auth/auth.service';
import { ConfigService } from '../services/config.service';
import { LayoutService } from '../services/layout.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './vertical-menu.component.html',
  styleUrls: ['./vertical-menu.component.scss']
})
export class VerticalMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  /* ---------------- Public Properties ---------------- */
  public menuItems: RouteInfo[] = [];
  public config: any = {};
  public collapseSidebar = false;

  public logoUrl = 'assets/img/logo.png';
  public logoTextUrl = 'assets/img/logo.text.png';

  /* ---------------- Private Properties ---------------- */
  private destroy$ = new Subject<void>();

  constructor(
    private configService: ConfigService,
    private layoutService: LayoutService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.config = this.configService.templateConf;
  }

  /* ---------------- Lifecycle Hooks ---------------- */
  ngOnInit(): void {
    // 1. Build Menu based on permissions
    this.menuItems = this.buildMenu(ROUTES);

    // 2. Subscribe to Config Changes
    this.configService.templateConf$
      .pipe(takeUntil(this.destroy$))
      .subscribe((conf) => {
        if (conf) {
          this.config = conf;
          this.updateLogoURLs();
          this.cdr.markForCheck();
        }
      });

    // 3. Initial Logo Setup
    this.updateLogoURLs();
  }

  ngAfterViewInit(): void {
    // Handle Window Resize for Layout responsiveness
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkLayout();
      });

    // Handle Sidebar Toggle Events from LayoutService
    this.layoutService.overlaySidebarToggle$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collapsed) => {
        if (this.config?.layout?.menuPosition === 'Side') {
          this.collapseSidebar = collapsed;
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ---------------- Logic Methods ---------------- */

  // Filter menu items based on user permissions
  private buildMenu(routes: RouteInfo[]): RouteInfo[] {
    const userPermits = this.authService.getPermits() || [];
    const userGroups = this.authService.getToken()?.user?.groups || [];

    // Admin bypass permit checks
    const isSuperUser = userGroups.some((g: string) => g === 'ADMIN');

    const filterFn = (items: RouteInfo[]): RouteInfo[] => {
      return items.reduce<RouteInfo[]>((acc, item) => {
        // 1. Check Permission
        let hasAccess = true;
        if (!isSuperUser && item.permits && item.permits.length > 0) {
          hasAccess = item.permits.some(p => userPermits.includes(p));
        }

        if (!hasAccess) return acc;

        // 2. Process Submenu (Recursion)
        const newItem = { ...item };
        if (newItem.submenu && newItem.submenu.length > 0) {
          newItem.submenu = filterFn(newItem.submenu);
          // If parents shouldn't show without children, uncomment next line:
          // if (newItem.submenu.length === 0) return acc; 
        }

        acc.push(newItem);
        return acc;
      }, []);
    };

    return filterFn(routes);
  }

  // Toggle Sidebar manually
  toggleSidebar(): void {
    if (this.config?.layout?.sidebar) {
      this.config.layout.sidebar.collapsed = !this.config.layout.sidebar.collapsed;
      this.configService.applyTemplateConfigChange({ layout: this.config.layout });
      // Dispatch resize event to force layout recalculations if needed
      setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    }
  }

  CloseSidebar(): void {
    this.layoutService.toggleSidebarSmallScreen(false);
  }

  // Update Logo based on theme
  private updateLogoURLs(): void {
    if (!this.config?.layout?.sidebar) return;

    const isWhiteBg = this.config.layout.sidebar.backgroundColor === 'white';
    if (isWhiteBg) {
      this.logoUrl = 'assets/img/logo.dark.png';
      this.logoTextUrl = 'assets/img/logo.text.dark.png';
    } else {
      this.logoUrl = 'assets/img/logo.png';
      this.logoTextUrl = 'assets/img/logo.text.png';
    }
  }

  private checkLayout(): void {
    this.updateLogoURLs();
    this.cdr.markForCheck();
  }
}
